import { useState } from 'react';

import { UserGatewayMessage } from '../../../../types/UserGatewayMessage';
import { UserStatus } from '../../../../types/UserStatus';
import { useDrawer } from '../../../cdl/Drawer/useDrawer.hook';
import { DropdownMenu, DropdownMenuOption } from '../../../cdl/DropdownMenu/DropdownMenu';
import { useToasts } from '../../../cdl/Toast/useToasts';
import { translate } from '../../../common/helpers/translate.helper';
import { useRole } from '../../../common/hooks/useRole';
import { DeactivateMemberModal } from '../../components/DeactivateMemberModal';
import { EditUser } from '../../components/EditUser';
import { useUserMutation } from '../hooks/useOptimisticUserMutation';

interface MemberDropdownProps {
    user: UserGatewayMessage;
}

export const MemberDropdown = ({ user }: MemberDropdownProps) => {
    const role = useRole();
    const userMutation = useUserMutation();
    const { push, pop } = useDrawer();
    const { addToast, addErrorToast } = useToasts();
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState<boolean>(false);

    if (role.user.id === user.id) {
        return null;
    }

    const isActive = user.status === UserStatus.ACTIVE;
    const isInvited = user.status === UserStatus.INVITED;
    const isSsoEnabled = user.ssoEnabled;

    const options: DropdownMenuOption[] = [];

    if (!isSsoEnabled) {
        options.push({
            label: translate('user.editMember'),
            onSelect: (event) => {
                event?.stopPropagation();
                const drawerId = push({
                    header: { title: translate('user.editMember') },
                    content: <EditUser user={user} onSuccess={() => pop(drawerId)} />,
                });
            },
        });
    }

    if (!isInvited && !isSsoEnabled) {
        if (isActive) {
            options.push({
                label: translate('user.setInactiveButton'),
                onSelect: () => {
                    setIsDeactivateModalOpen(true);
                },
            });
        } else {
            options.push({
                label: translate('user.setActiveButton'),
                onSelect: () => {
                    userMutation.mutate(
                        {
                            userId: user.id,
                            userMessage: {
                                ...user,
                                status: UserStatus.ACTIVE,
                            },
                        },
                        {
                            onSuccess: () => {
                                addToast(translate('user.editUserToasts.success'));
                            },
                            onError: () => {
                                addErrorToast(translate('user.editUserToasts.error'));
                            },
                        }
                    );
                },
            });
        }
    }

    if (options.length === 0) {
        return null;
    }

    return (
        <div>
            <DropdownMenu align="end" options={options} />
            <DeactivateMemberModal
                isOpen={isDeactivateModalOpen}
                onDismiss={() => setIsDeactivateModalOpen(false)}
                user={user}
            />
        </div>
    );
};
